import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  Col,
  Empty,
  InputNumber,
  List,
  Row,
  Space,
  Spin,
  Statistic,
  Tag,
} from 'antd';
import { Table } from 'antd';
import { useState } from 'react';
import { ScheduleToggle } from '@/components/ScheduleToggle';
import { SalesConnectorCard } from '@/features/sales/ui/SalesConnectorCard';
import {
  analyticsApi,
  type MetaCampaignInsight,
  type MetricInput,
  type Recommendation,
} from '../analytics.api';

const CATEGORY_COLORS: Record<string, string> = {
  promote_product: 'green',
  stop_promoting: 'red',
  increase_frequency: 'blue',
  bundle: 'purple',
  seasonal: 'orange',
  general: 'default',
};

export function AnalyticsTab({
  orgId,
  projectId,
  canEdit,
}: {
  orgId: string;
  projectId: string;
  canEdit: boolean;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const summaryKey = ['analytics-summary', orgId, projectId] as const;
  const recsKey = ['recommendations', orgId, projectId] as const;

  const summaryQuery = useQuery({
    queryKey: summaryKey,
    queryFn: () => analyticsApi.summary(orgId, projectId),
  });
  const recsQuery = useQuery({
    queryKey: recsKey,
    queryFn: () => analyticsApi.listRecommendations(orgId, projectId),
  });

  const [metric, setMetric] = useState<MetricInput>({});

  const ingest = useMutation({
    mutationFn: () => analyticsApi.ingest(orgId, projectId, metric),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: summaryKey });
      setMetric({});
      message.success('Metrics logged');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to log metrics'),
  });

  const analyze = useMutation({
    mutationFn: () => analyticsApi.analyze(orgId, projectId),
    onSuccess: () => {
      message.success('Analysis queued — recommendations appear once the worker runs');
      setTimeout(() => void queryClient.invalidateQueries({ queryKey: recsKey }), 3000);
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to analyze'),
  });

  const syncMeta = useMutation({
    mutationFn: () => analyticsApi.syncMeta(orgId, projectId),
    onSuccess: () => {
      message.success('Meta sync queued — pulls insights if a Meta account is connected');
      setTimeout(() => void queryClient.invalidateQueries({ queryKey: summaryKey }), 3000);
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to sync'),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'accepted' | 'dismissed' }) =>
      analyticsApi.updateRec(orgId, projectId, id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: recsKey });
      message.success('Updated');
    },
  });

  const briefing = useQuery({
    queryKey: ['analyst-briefing', orgId, projectId] as const,
    queryFn: () => analyticsApi.briefing(orgId, projectId),
  });

  const metaCampaigns = useQuery({
    queryKey: ['meta-campaign-insights', orgId, projectId] as const,
    queryFn: () => analyticsApi.metaCampaigns(orgId, projectId),
  });

  const kpis = summaryQuery.data?.summary.kpis;
  const totals = summaryQuery.data?.summary.totals;

  const numberField = (label: string, field: keyof MetricInput) => (
    <Col>
      <Space direction="vertical" size={2}>
        <span style={{ fontSize: 12, color: '#6b7280' }}>{label}</span>
        <InputNumber
          min={0}
          value={metric[field] ?? null}
          onChange={(v) => setMetric((m) => ({ ...m, [field]: v ?? undefined }))}
          style={{ width: 110 }}
        />
      </Space>
    </Col>
  );

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <SalesConnectorCard orgId={orgId} projectId={projectId} canEdit={canEdit} />
      {summaryQuery.isLoading ? (
        <Spin />
      ) : (
        <Card
          size="small"
          title={`Performance (last ${summaryQuery.data?.summary.windowDays ?? 30} days)`}
        >
          <Row gutter={24}>
            <Col span={6}>
              <Statistic title="Reach" value={totals?.reach ?? 0} />
            </Col>
            <Col span={6}>
              <Statistic title="Sales" value={totals?.sales ?? 0} prefix="$" />
            </Col>
            <Col span={6}>
              <Statistic
                title="Engagement rate"
                value={(kpis?.engagementRate ?? 0) * 100}
                suffix="%"
                precision={2}
              />
            </Col>
            <Col span={6}>
              <Statistic title="ROAS" value={kpis?.roas ?? 0} precision={2} />
            </Col>
          </Row>
        </Card>
      )}

      {briefing.data ? (
        <Card
          size="small"
          title={
            <Space>
              Analyst briefing
              <Tag color={briefing.data.source === 'llm' ? 'purple' : 'default'}>
                {briefing.data.source}
              </Tag>
              <span style={{ fontWeight: 400, fontSize: 12, color: '#6b7280' }}>
                {new Date(briefing.data.createdAt).toLocaleString()}
              </span>
            </Space>
          }
        >
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div>
              <strong>What I'm seeing:</strong>
              <div style={{ whiteSpace: 'pre-wrap' }}>{briefing.data.thinking}</div>
            </div>
            {briefing.data.nextSteps.length > 0 ? (
              <div>
                <strong>Next steps:</strong>
                <ol style={{ margin: '4px 0 0', paddingInlineStart: 20 }}>
                  {briefing.data.nextSteps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </div>
            ) : null}
          </Space>
        </Card>
      ) : null}

      <Card size="small" title="Meta campaigns (last 30 days)">
        <Table<MetaCampaignInsight>
          rowKey="campaignId"
          size="small"
          loading={metaCampaigns.isLoading}
          dataSource={metaCampaigns.data ?? []}
          locale={{ emptyText: 'No campaign data yet — connect Meta and run a sync' }}
          pagination={false}
          columns={[
            { title: 'Campaign', dataIndex: 'campaignName', ellipsis: true },
            { title: 'Spend', dataIndex: 'spend', render: (v: number) => v.toFixed(2) },
            { title: 'Sales', dataIndex: 'sales', render: (v: number) => v.toFixed(2) },
            {
              title: 'ROAS',
              dataIndex: 'roas',
              render: (v: number) => (
                <Tag color={v === 0 ? 'default' : v < 1 ? 'red' : v >= 3 ? 'green' : 'blue'}>
                  {v.toFixed(2)}
                </Tag>
              ),
            },
            { title: 'Clicks', dataIndex: 'clicks' },
            { title: 'Reach', dataIndex: 'reach' },
          ]}
        />
      </Card>

      {canEdit ? (
        <Card size="small" title="Log metrics">
          <Row gutter={[16, 16]} align="bottom" wrap>
            {numberField('Reach', 'reach')}
            {numberField('Impressions', 'impressions')}
            {numberField('Clicks', 'clicks')}
            {numberField('Engagements', 'engagements')}
            {numberField('Conversions', 'conversions')}
            {numberField('Sales ($)', 'sales')}
            {numberField('Spend ($)', 'spend')}
            <Col>
              <Button type="primary" onClick={() => ingest.mutate()} loading={ingest.isPending}>
                Log
              </Button>
            </Col>
          </Row>
        </Card>
      ) : null}

      {canEdit ? (
        <Space wrap>
          <Button type="primary" onClick={() => analyze.mutate()} loading={analyze.isPending}>
            Run analysis now
          </Button>
          <Button onClick={() => syncMeta.mutate()} loading={syncMeta.isPending}>
            Sync Meta insights
          </Button>
          <Button onClick={() => queryClient.invalidateQueries({ queryKey: recsKey })}>
            Refresh
          </Button>
          <ScheduleToggle
            orgId={orgId}
            projectId={projectId}
            kind="analyze"
            label="Autonomous analysis daily"
            hint="When on, the Business Analyst reviews performance and posts recommendations daily."
            canEdit={canEdit}
          />
          <ScheduleToggle
            orgId={orgId}
            projectId={projectId}
            kind="strategize"
            label="Growth strategist weekly"
            hint="When on, the Growth Strategist studies sales, campaign history, and product rotation weekly, sets the focus-product strategy that content follows, and dispatches spotlight campaigns."
            canEdit={canEdit}
          />
          <ScheduleToggle
            orgId={orgId}
            projectId={projectId}
            kind="sync_meta_insights"
            label="Auto-sync Meta daily"
            hint="When on, live Meta insights are pulled into analytics every day."
            canEdit={canEdit}
          />
        </Space>
      ) : null}

      <Card size="small" title="Recommendations">
        {recsQuery.isLoading ? (
          <Spin />
        ) : (recsQuery.data ?? []).length === 0 ? (
          <Empty description="No recommendations yet — run an analysis" />
        ) : (
          <List
            dataSource={recsQuery.data ?? []}
            renderItem={(r: Recommendation) => (
              <List.Item
                actions={
                  canEdit && r.status === 'open'
                    ? [
                        <Button
                          key="accept"
                          size="small"
                          type="primary"
                          onClick={() => setStatus.mutate({ id: r.id, status: 'accepted' })}
                        >
                          Accept
                        </Button>,
                        <Button
                          key="dismiss"
                          size="small"
                          onClick={() => setStatus.mutate({ id: r.id, status: 'dismissed' })}
                        >
                          Dismiss
                        </Button>,
                      ]
                    : [<Tag key="s">{r.status}</Tag>]
                }
              >
                <List.Item.Meta
                  title={
                    <Space wrap>
                      {r.title}
                      <Tag color={CATEGORY_COLORS[r.category] ?? 'default'}>{r.category}</Tag>
                      {r.targetAgent ? <Tag>→ {r.targetAgent}</Tag> : null}
                      <Tag color={r.source === 'llm' ? 'gold' : 'default'}>{r.source}</Tag>
                    </Space>
                  }
                  description={r.body}
                />
              </List.Item>
            )}
          />
        )}
      </Card>
    </Space>
  );
}
